import { Component, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  DisponibiliteEpargne,
  FicheConseil,
  FondsEvenementiel,
  FondsEvenementielConseil,
  FrequenceVersement,
  ModeDeGestion,
  ModeDeGestionConfig,
  OptionAutomatique,
  OptionAutomatiqueConseil,
  Versements,
  VpModeOptions,
} from '@lib/models/generated/graphql';
import { NatureOperationId } from '@lib/models/graphqlData';
import { OuiBannerDeclination } from 'omnium-ui/banner';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiValidationError } from 'omnium-ui/form-field';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FondsEvenementielService } from 'src/app/services/fonds-evenementiel.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { OptionAutomatiqueService } from 'src/app/services/option-automatique.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';
type OptionAutoWithOrderConseil = OptionAutomatiqueConseil & { ordre: number };
type FondsEvenementielWithOrderConseil = FondsEvenementielConseil & { ordre: number };
@Component({
  selector: 'app-mode-de-gestion',
  templateUrl: './mode-de-gestion.component.html',
  styleUrls: ['./mode-de-gestion.component.scss'],
})
export class ModeDeGestionComponent extends AbstractStepComponent {
  @Input() arbitrageVP: FieldFormControl<boolean, boolean> | undefined;

  readonly libreModeOnlyDispoEpargne = DisponibiliteEpargne.ToujoursDispo;
  readonly libreModeOnlyTolerancePerteCode = 'NON'; //FIXME: use des vrais valeurs

  readonly someOptionsDisabledTitle = 'Certains modes de gestions ont été désactivés';
  readonly allOptionsDisabledTitle = 'Tous les modes de gestions ont été désactivés';

  readonly someOptionsDisabledDeclination = 'info';
  readonly allOptionsDisabledDeclination = 'error';

  readonly requiredValueError: OuiValidationError = {
    code: 'required',
    message: 'Ce champs est requis !',
  };

  modeDeGestionOptions: OuiButtonToggleOption<ModeDeGestion>[];

  modeDeGestionControl: FieldFormControl<ModeDeGestion | null, ModeDeGestion | null>;

  modesPossibles: ModeDeGestionConfig[] = [];
  validModes: ModeDeGestion[] = [];

  messages: string[] = [];
  bannerDeclination: OuiBannerDeclination = this.someOptionsDisabledDeclination;
  bannerTitle: string = this.someOptionsDisabledTitle;
  modeSubscription: Subscription | null = null;
  showDisponibiliteEpargneMessage: boolean = false;

  optionsAutoWithOrder: OptionAutoWithOrderConseil[] = [];
  fondsEvenementielsWithOrder: FondsEvenementielWithOrderConseil[] = [];
  stepTouched = false;

  public override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
    if (previousFicheConseil?.produit?.id !== newFicheConseil?.produit?.id) {
      {
        this.modesPossibles = this.formConfigService.getSelectedProduit()?.gestionPossibles ?? [];
        if (previousFicheConseil?.produit?.id) {
          this.modeDeGestionControl?.setValue(null);
        }
      }
    }

    if (
      newFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire ||
      newFicheConseil?.natureOperation?.id === NatureOperationId.ChangementModeGestion
    ) {
      this.modesPossibles = this.formConfigService.getSelectedProduit()?.gestionPossibles ?? [];

      this.handleModeDeGestionPossibles(newFicheConseil);
    }

    if (
      (newFicheConseil?.versements &&
        this.isVersementDataChanged(newFicheConseil.versements, previousFicheConseil?.versements ?? null)) ||
      (newFicheConseil?.disponibiliteEpargne && this.isKYCDataChanged(newFicheConseil, previousFicheConseil ?? null))
    ) {
      this.handleModeDeGestionPossibles(newFicheConseil);
    }
  }
  public override isStepValid(): boolean {
    let isOptionAutoChecked = true;
    if (this.arbitrageVP && this.arbitrageVP.value === true) {
      isOptionAutoChecked = this.currentFicheConseil?.optionsAutomatiques?.some(option => option.isSelected) ?? false;
    }
    return (
      this.modeDeGestionControl?.errors === null && this.modeDeGestionControl?.value !== null && isOptionAutoChecked
    );
  }
  public override markAsTouched(): void {
    this.modeDeGestionControl?.markAsTouched();
    this.stepTouched = true;
  }
  constructor(
    protected override ficheConseilService: FicheConseilService,
    protected formConfigService: FormConfigService,
    protected optionAutomatiqueService: OptionAutomatiqueService,
    protected fondsEvenementielService: FondsEvenementielService
  ) {
    super(ficheConseilService);
  }
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modeSubscription?.unsubscribe();
  }
  override ngOnInit(): void {
    super.ngOnInit();
    this.modesPossibles = this.formConfigService.getSelectedProduit()?.gestionPossibles ?? [];
    if (this.currentFicheConseil) {
      this.handleModeDeGestionPossibles(this.currentFicheConseil);
    }
    this.modeDeGestionControl = new FieldFormControl<ModeDeGestion | null, ModeDeGestion | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      ficheDataToFieldDataWrapper: (ficheData: ModeDeGestion | null) => {
        if (ficheData) {
          const found = this.modeDeGestionOptions.find(option => option.value.code === ficheData.code)?.value ?? null;
          if (found) return found;
        }
        return null;
      },
      dataKey: 'modeDeGestion',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });
    this.checkOnlyOneOptionRule();
    this.checkIfSelectedValueIsNowDisabledThenReset();
    if (this.modeDeGestionControl.value) {
      this.setOptionsAutoWithOrder(this.modeDeGestionControl.value);
      this.setFondsEvenementielsWithOrder(this.modeDeGestionControl.value);
    }
    // listen to changes of the mode de gestion selection
    // if the user selects a new mode de gestion, we need to update the options automatiques
    // (the options automatiques are the options that are specific to the produit and the mode de gestion)
    // we compare the ids of the current options automatiques with the ids of the new options automatiques
    // if they are different, we update the options automatiques on the fiche conseil
    this.modeSubscription = this.modeDeGestionControl.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
      this.optionAutomatiqueService.loadOptionsAutomatiquesByProduit(this.currentFicheConseil?.produit?.id);
      const optionsAuto = this.optionAutomatiqueService.optionsAutomatiques.length;
      if ((value?.code === 'LIBRE' || value === null || value === undefined) && optionsAuto > 0) {
        this.arbitrageVP?.enable();
      } else {
        this.arbitrageVP?.setValue(false);
        this.arbitrageVP?.disable();
      }

      this.setOptionsAutoWithOrder(value);
      this.setFondsEvenementielsWithOrder(value);
      if (value) {
        this.ficheConseilService.onModeDeGestionChanged(value);
      }
    });
    this.arbitrageVP?.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
      if (value === false) {
        this.currentFicheConseil?.optionsAutomatiques?.forEach(option => (option.isSelected = false));
      }
    });
  }

  get modeDeGestionTitle(): string {
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.ChangementModeGestion) {
      return 'Nouveau mode de gestion';
    }

    return 'Mode de gestion';
  }

  setOptionsAutoWithOrder(modeDeGestion: ModeDeGestion): void {
    const ficheOptions = this.currentFicheConseil?.optionsAutomatiques ?? [];
    const automatiques = modeDeGestion?.code
      ? this.optionAutomatiqueService.getOptionAutomatiqueByModeGestion(modeDeGestion.code)
      : [];
    const areOptionsDifferent = !this.compareIds(ficheOptions, automatiques);
    if (areOptionsDifferent) {
      const optionAutoConseils: OptionAutomatiqueConseil[] =
        automatiques?.map(
          automatique =>
            ({
              id: automatique.id,
              libelle: automatique.libelle,
              isSelected: false,
            } as OptionAutomatiqueConseil)
        ) ?? [];

      this.ficheConseilService.onFicheConseilDataEdit('optionsAutomatiques', optionAutoConseils);
    }
    const currentOptions = this.currentFicheConseil?.optionsAutomatiques ?? [];
    this.optionsAutoWithOrder = this.mapFicheOptionToOptionWithOrder(currentOptions, automatiques);
  }

  setFondsEvenementielsWithOrder(modeDeGestion: ModeDeGestion): void {
    const ficheFonds = this.currentFicheConseil?.fondsEvenementiels ?? [];
    const fondsFromConfig = modeDeGestion?.code
      ? this.fondsEvenementielService.getFondsEvenementielsByModeGestion(modeDeGestion.code)
      : [];
    const areFondsDifferent = !this.compareIds(ficheFonds, fondsFromConfig);
    if (areFondsDifferent) {
      const fondsConseils: FondsEvenementielConseil[] =
        fondsFromConfig?.map(
          fonds => {
            const currentFonds = ficheFonds.find(
              ficheFond => ficheFond.id === fonds.id || ficheFond.code === fonds.code
            );
            return {
              id: fonds.id,
              code: fonds.code,
              libelle: fonds.libelle,
              isSelected: currentFonds?.isSelected ?? false,
            } as FondsEvenementielConseil;
          }
        ) ?? [];

      this.ficheConseilService.onFicheConseilDataEdit('fondsEvenementiels', fondsConseils);
    }
    const currentFonds = this.currentFicheConseil?.fondsEvenementiels ?? [];
    this.fondsEvenementielsWithOrder = this.mapFicheFondsToFondsWithOrder(currentFonds, fondsFromConfig);
  }

  mapFicheFondsToFondsWithOrder(
    ficheFonds: FondsEvenementielConseil[],
    fondsFromConfig: FondsEvenementiel[]
  ): FondsEvenementielWithOrderConseil[] {
    return ficheFonds.map(fonds => {
      const found = fondsFromConfig.find(fondsWithOrder => fondsWithOrder.id === fonds.id);
      if (found) {
        return {
          ...fonds,
          ordre: found.ordre,
        };
      }
      return {
        ...fonds,
        ordre: 9999999,
      };
    });
  }

  mapFicheOptionToOptionWithOrder(
    ficheOptions: OptionAutomatiqueConseil[],
    optionsFromConfig: OptionAutomatique[]
  ): OptionAutoWithOrderConseil[] {
    return ficheOptions.map(option => {
      const found = optionsFromConfig.find(optionWithOrder => optionWithOrder.id === option.id);
      if (found) {
        return {
          ...option,
          ordre: found.ordre,
        };
      }
      return {
        ...option,
        ordre: 9999999, // put the options that are not in the config at the end (this should not happen)
      };
    });
  }

  isKYCDataChanged(newFicheConseil: FicheConseil, previousFicheConseil: FicheConseil | null): boolean {
    const newTolerancePerte = newFicheConseil.investisseur?.profil?.hasTolerancePerte;
    const previousTolerancePerte = previousFicheConseil?.investisseur?.profil?.hasTolerancePerte;

    const newDispoEpargne = newFicheConseil.disponibiliteEpargne;
    const previousDispoEpargne = previousFicheConseil?.disponibiliteEpargne;

    return newTolerancePerte !== previousTolerancePerte || newDispoEpargne !== previousDispoEpargne;
  }
  handleModeDeGestionPossibles(fiche: FicheConseil) {
    const { options, messages } = this.buildModeDeGestionOptionsAndMessages(this.modesPossibles, fiche);
    this.modeDeGestionOptions = options;
    this.messages = messages;
    this.checkBannerDeclinationAndTitle();

    if (this.modeDeGestionControl) {
      this.checkOnlyOneOptionRule();
      this.checkIfSelectedValueIsNowDisabledThenReset();
    }
  }

  isOptionDisabledByKYC(mode: ModeDeGestionConfig, isLibreModeOnly: boolean): boolean {
    if (isLibreModeOnly) {
      return mode.code !== 'LIBRE';
    }
    return false;
  }
  buildModeDeGestionOptionsAndMessages(
    gestionPossibles: ModeDeGestionConfig[],
    fiche: FicheConseil
  ): {
    options: OuiButtonToggleOption<ModeDeGestion>[];
    messages: string[];
  } {
    {
      const options: OuiButtonToggleOption<ModeDeGestion>[] = [];
      const messages: string[] = [];
      const avecTransfert: boolean = !!fiche.versements?.souscriptionAvecTransfert;

      const avecVersementProgramme: boolean = !!fiche.versements?.avecVersementProgramme;
      const montantVersementInitial = fiche.versements.versementInitial?.montantBrut ?? 0;

      const versementProgramme = fiche.versements.versementProgramme;

      const montantVersementComplementaire = fiche.versements.versementComplementaire?.montantBrut ?? 0;
      const montantVersementProgramme = fiche.versements.versementProgramme?.versement?.montantBrut ?? 0;

      //FIXME business wants to supress the rules on disponibilite Eparge for now.
      this.showDisponibiliteEpargneMessage = false;

      //FIXME waiting for spec for tolerance perte with new profil data: US23072 https://omnium-finance.visualstudio.com/ConseilPlacement/_workitems/edit/23072
      // this should be specified later: here is temporary solution (hasTolerancePerte is always true):
      const isLibreModeOnlyByTolerancePerte = false;

      if (isLibreModeOnlyByTolerancePerte) {
        messages.push(
          'La capacité à supporter les pertes issues du profil investisseur impose un mode de gestion libre.'
        );
      }

      gestionPossibles.forEach((modeConfig: ModeDeGestionConfig) => {
        const option: OuiButtonToggleOption<ModeDeGestion> = {
          value: { code: modeConfig.code, libelle: modeConfig.libelle },
          label: modeConfig.libelle,
          disabled: this.isOptionDisabledByKYC(
            modeConfig,
            this.showDisponibiliteEpargneMessage || isLibreModeOnlyByTolerancePerte
          ),
        };

        if (!option.disabled) {
          if (modeConfig.conditionVersementInitial) {
            const transfertImpossible: boolean = modeConfig.conditionVersementInitial?.montantMinAvecTransfert == null;

            const vpImpossible: boolean = modeConfig.conditionVersementInitial?.montantMinAvecVP === null;

            // no transfert possible in modeDeGestion
            if (transfertImpossible && fiche.natureOperation?.id === NatureOperationId.Souscription) {
              if (avecVersementProgramme) {
                if (vpImpossible) {
                  option.disabled = true;
                  messages.push(
                    `En <span class="subtitle-2">${modeConfig.libelle}</span> pour ce produit il n'est pas possible de mettre en place des versements programmés.`
                  );
                }
                if (
                  !vpImpossible &&
                  montantVersementInitial < (modeConfig.conditionVersementInitial?.montantMinAvecVP ?? 0)
                ) {
                  option.disabled = true;
                  messages.push(
                    `En <span class="subtitle-2">${
                      modeConfig.libelle
                    }</span> pour ce produit le montant du versement initial ne peut pas être en dessous de ${
                      modeConfig.conditionVersementInitial?.montantMinAvecVP ?? 1
                    }€ avec versement programmé.`
                  );
                }
              }
              if (!avecVersementProgramme) {
                if (montantVersementInitial < (modeConfig.conditionVersementInitial?.montantMinSouscription ?? 1)) {
                  option.disabled = true;
                  messages.push(
                    `En <span class="subtitle-2">${
                      modeConfig.libelle
                    }</span> pour ce produit le montant du versement initial ne peut pas être en dessous de ${
                      modeConfig.conditionVersementInitial?.montantMinSouscription ?? 1
                    }€ sans versement programmé.`
                  );
                }
              }
            }

            // transfert possible in modeDeGestion
            if (!transfertImpossible && fiche.natureOperation?.id === NatureOperationId.Souscription) {
              if (!avecTransfert) {
                if (avecVersementProgramme) {
                  if (vpImpossible) {
                    option.disabled = true;
                    messages.push(
                      `En <span class="subtitle-2">${modeConfig.libelle}</span> pour ce produit, il n'est pas possible de réaliser des versements programmés.`
                    );
                  }
                  if (montantVersementInitial < (modeConfig.conditionVersementInitial?.montantMinAvecVP ?? 1)) {
                    option.disabled = true;
                    messages.push(
                      `En <span class="subtitle-2">${
                        modeConfig.libelle
                      }</span> pour ce produit le montant du versement initial ne peut pas être en dessous de ${
                        modeConfig.conditionVersementInitial?.montantMinAvecVP ?? 1
                      }€ avec versement programmé et sans transfert.`
                    );
                  }
                }
                if (!avecVersementProgramme) {
                  if (montantVersementInitial < (modeConfig.conditionVersementInitial?.montantMinSouscription ?? 1)) {
                    option.disabled = true;
                    messages.push(
                      `En <span class="subtitle-2">${
                        modeConfig.libelle
                      }</span> pour ce produit le montant du versement initial ne peut pas être en dessous de ${
                        modeConfig.conditionVersementInitial?.montantMinSouscription ?? 1
                      }€ sans versement programmé ou transfert.`
                    );
                  }
                }
              }
              if (avecTransfert) {
                if (montantVersementInitial < (modeConfig.conditionVersementInitial?.montantMinAvecTransfert ?? 1)) {
                  option.disabled = true;
                  messages.push(
                    `En <span class="subtitle-2">${
                      modeConfig.libelle
                    }</span> pour ce produit le montant du versement initial ne peut pas être en dessous de ${
                      modeConfig.conditionVersementInitial?.montantMinAvecTransfert ?? 1
                    }€ avec transfert.`
                  );
                }
              }
            }
          }

          if (fiche.natureOperation?.id === NatureOperationId.VersementComplementaire) {
            if (montantVersementComplementaire < (modeConfig.conditionVersementComplementaire?.montantMinVC ?? 1)) {
              option.disabled = true;
              messages.push(
                `En <span class="subtitle-2">${
                  modeConfig.libelle
                }</span> pour ce produit le montant du versement complémentaire ne peut pas être en dessous de ${
                  modeConfig.conditionVersementComplementaire?.montantMinVC ?? 1
                }€`
              );
            }

            if (modeConfig.conditionVersementProgramme && fiche?.vpModeOptions) {
              const montantMinParFrequence = {
                [FrequenceVersement.Mensuel]: modeConfig.conditionVersementProgramme.montantMinMensuel,
                [FrequenceVersement.Annuel]: modeConfig.conditionVersementProgramme.montantMinAnnuel,
                [FrequenceVersement.Trimestriel]: modeConfig.conditionVersementProgramme.montantMinTrimestriel,
                [FrequenceVersement.Semestriel]: modeConfig.conditionVersementProgramme.montantMinSemestriel,
              };

              const montantMin = montantMinParFrequence[versementProgramme?.frequence ?? FrequenceVersement.Mensuel];

              if (
                fiche?.vpModeOptions === VpModeOptions.MiseEnPlace ||
                (fiche?.vpModeOptions === VpModeOptions.Modification &&
                  fiche?.versements?.versementProgramme?.isMontantBrutEditable === true)
              ) {
                if ((montantMin ?? 1) > (montantVersementProgramme ?? 0)) {
                  option.disabled = true;
                  messages.push(`
                    <span class="subtitle-2">${modeConfig.libelle}</span> 
                    pour ce produit le montant des versements programmés ne peut pas être en dessous de ${montantMin} ${
                    versementProgramme?.frequence?.toLowerCase() || 'mensuel'
                  }.    `);
                }
              }
            }
          }

          //souscription versement programme
          if (avecVersementProgramme && fiche.natureOperation?.id === NatureOperationId.Souscription) {
            switch (versementProgramme?.frequence) {
              case FrequenceVersement.Mensuel:
                if (
                  versementProgramme.versement.montantBrut <
                  (modeConfig.conditionVersementProgramme?.montantMinMensuel ?? 1)
                ) {
                  option.disabled = true;
                  messages.push(
                    this.messageFrequenceMontantMin(
                      modeConfig.libelle,
                      modeConfig.conditionVersementProgramme?.montantMinMensuel ?? 1,
                      FrequenceVersement.Mensuel
                    )
                  );
                }
                break;
              case FrequenceVersement.Trimestriel:
                if (
                  versementProgramme.versement.montantBrut <
                  (modeConfig.conditionVersementProgramme?.montantMinTrimestriel ?? 0)
                ) {
                  option.disabled = true;
                  messages.push(
                    this.messageFrequenceMontantMin(
                      modeConfig.libelle,
                      modeConfig.conditionVersementProgramme?.montantMinTrimestriel ?? 0,
                      FrequenceVersement.Trimestriel
                    )
                  );
                }
                break;
              case FrequenceVersement.Semestriel:
                if (
                  versementProgramme.versement.montantBrut <
                  (modeConfig.conditionVersementProgramme?.montantMinSemestriel ?? 1)
                ) {
                  option.disabled = true;
                  messages.push(
                    this.messageFrequenceMontantMin(
                      modeConfig.libelle,
                      modeConfig.conditionVersementProgramme?.montantMinSemestriel ?? 1,
                      FrequenceVersement.Semestriel
                    )
                  );
                }
                break;
              case FrequenceVersement.Annuel:
                if (
                  versementProgramme.versement.montantBrut <
                  (modeConfig.conditionVersementProgramme?.montantMinAnnuel ?? 1)
                ) {
                  option.disabled = true;
                  messages.push(
                    this.messageFrequenceMontantMin(
                      modeConfig.libelle,
                      modeConfig.conditionVersementProgramme?.montantMinAnnuel ?? 1,
                      FrequenceVersement.Annuel
                    )
                  );
                }
                break;
              case null:
                option.disabled = true;
                messages.push(
                  this.messageFrequenceMontantMin(
                    modeConfig.libelle,
                    modeConfig.conditionVersementProgramme?.montantMinMensuel ?? 1,
                    FrequenceVersement.Mensuel
                  )
                );
                messages.push(
                  this.messageFrequenceMontantMin(
                    modeConfig.libelle,
                    modeConfig.conditionVersementProgramme?.montantMinTrimestriel ?? 1,
                    FrequenceVersement.Trimestriel
                  )
                );
                messages.push(
                  this.messageFrequenceMontantMin(
                    modeConfig.libelle,
                    modeConfig.conditionVersementProgramme?.montantMinSemestriel ?? 1,
                    FrequenceVersement.Semestriel
                  )
                );
                messages.push(
                  this.messageFrequenceMontantMin(
                    modeConfig.libelle,
                    modeConfig.conditionVersementProgramme?.montantMinAnnuel ?? 1,
                    FrequenceVersement.Annuel
                  )
                );
                break;
            }
          }
        }
        options.push(option);
      });
      return { options, messages };
    }
  }
  messageFrequenceMontantMin(modeLibelle: string, montantMin: number, frequence: FrequenceVersement): string {
    return `En <span class="subtitle-2">${modeLibelle}</span> pour ce produit le montant des versements programmés ne peut pas être en dessous de ${montantMin}€ ${frequence.toLocaleLowerCase()}.`;
  }
  checkIfSelectedValueIsNowDisabledThenReset() {
    if (this.modeDeGestionControl?.value) {
      const selectedOption = this.modeDeGestionOptions.find(
        option => option.value.code === this.modeDeGestionControl.value?.code
      );
      if (selectedOption && selectedOption.disabled) {
        this.modeDeGestionControl?.setValue(null);
        this.modeDeGestionControl?.updateValueAndValidity();
        this.modeDeGestionControl?.markAsTouched();
      }
    }
  }
  checkOnlyOneOptionRule() {
    if (!this.isAllOptionsDisabled() && this.modeDeGestionOptions.length === 1) {
      this.modeDeGestionControl?.setValue(this.modeDeGestionOptions[0].value);
      this.modeDeGestionControl?.updateValueAndValidity();
      this.modeDeGestionControl?.markAsTouched();
    }
  }
  checkBannerDeclinationAndTitle() {
    if (this.isAllOptionsDisabled()) {
      this.bannerTitle = this.allOptionsDisabledTitle;
      this.bannerDeclination = this.allOptionsDisabledDeclination;
      return;
    }
    this.bannerDeclination = this.someOptionsDisabledDeclination;
    this.bannerTitle = this.someOptionsDisabledTitle;
  }
  isAllOptionsDisabled(): boolean {
    return this.modeDeGestionOptions.every(option => option.disabled);
  }

  isVersementDataChanged(newVersementData: Versements | null, previousVersementData: Versements | null): boolean {
    return (
      newVersementData?.souscriptionAvecTransfert != previousVersementData?.souscriptionAvecTransfert ||
      newVersementData?.versementInitial?.montantBrut != previousVersementData?.versementInitial?.montantBrut ||
      newVersementData?.avecVersementProgramme != previousVersementData?.avecVersementProgramme ||
      newVersementData?.versementProgramme?.frequence != previousVersementData?.versementProgramme?.frequence ||
      newVersementData?.versementProgramme?.versement?.montantBrut !=
        previousVersementData?.versementProgramme?.versement?.montantBrut ||
      newVersementData?.versementProgramme?.isMontantBrutEditable !==
        previousVersementData?.versementProgramme?.isMontantBrutEditable
    );
  }

  /**
   * Compare two arrays of objects with an "id" property to see if they contain the same set of ids.
   * @param listA the first array of objects
   * @param listB the second array of objects
   * @returns true if the two arrays contain the same set of ids, false otherwise
   */
  compareIds(listA: { id: string }[], listB: { id: string }[]): boolean {
    const ids1 = new Set(listA.map(item => item.id));
    const ids2 = new Set(listB.map(item => item.id));
    return ids1.size === ids2.size && [...ids1].every(id => ids2.has(id));
  }
}
